import { shop } from "@/repositories/shop";
import { LayoutAccess } from "@/types/v2";
import { useQuery, useMutation, useQueryClient } from "react-query";

/**
 * Query key factory for layout access
 */
export const layoutAccessQueryKey = (shopId: string) => [
  "layoutAccess",
  shopId,
];

/**
 * Query configuration for fetching layout access
 */

/**
 * Hook to fetch layout access independently from the store subscription.
 * This allows layout access to be fetched without being affected by the
 * isEditing guard that protects layout content from real-time updates.
 */
export const useLayoutAccessQuery = (shopId: string | undefined) => {
  return useQuery<LayoutAccess[]>(
    layoutAccessQueryKey(shopId!),
    async () => {
      const shopDoc = await shop.get(shopId!);
      return shopDoc?.layoutAccess ?? [];
    },
    {
      enabled: !!shopId, // Only run query if shopId exists
      refetchOnWindowFocus: false,
      staleTime: 0, // Always fetch fresh data
    }
  );
};

/**
 * Hook returning a callback to invalidate the layout access cache.
 * Needed by mutations that touch `layoutAccess` outside of
 * useUpdateLayoutAccessMutation (e.g. a layout rename migrating the
 * `layoutName` on existing access entries server-side).
 */
export const useInvalidateLayoutAccessQuery = (shopId: string | undefined) => {
  const queryClient = useQueryClient();

  return () => {
    if (shopId) {
      queryClient.invalidateQueries(layoutAccessQueryKey(shopId));
    }
  };
};

/**
 * Hook to update layout access with automatic cache invalidation
 */
export const useUpdateLayoutAccessMutation = (shopId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (layoutAccess: LayoutAccess[]) => {
      if (!shopId) throw new Error("Shop ID is required");
      return shop.updateLayoutAccess(shopId, layoutAccess);
    },
    onSuccess: () => {
      // Invalidate and refetch layout access after successful update
      if (shopId) {
        queryClient.invalidateQueries(layoutAccessQueryKey(shopId));
      }
    },
  });
};
